import { formatCurrency } from "../../../utils/numberUtils";
import { RoundToTwoDecimalPlaces } from "../../../utils/numberUtils";

export const getBlankQuoteItem = (guid = crypto.randomUUID()) => {
    return {
        id: 0,
        guid,
        internalName: "",
        nameOnQuote: "",
        originalNameOnQuote: "",
        upfrontNameOnQuote: "",
        quoteGroupDescription: "",
        itemLookupId: null,
        supplierId: null,
        itemLookupVersionId: null,
        itemLookupTypeId: null,
        quantity: 0,
        isCreditItem: false,
        quoteModuleGuid: null,
        isUpfront: false,
        upfrontCost: 0,
        upfrontPrice: 0,
        isRecurring: false,
        recurringNameOnQuote: "",
        recurringCost: 0,
        recurringPrice: 0,
        recurringStartMonth: 1,
        recurringIntervalMonths: 0,
        recurringNumPayments: 0,
        typeCustomAttributes: [],
        customAttributes: [],
        tags: [],
        autotaskCode: "",
        salesforceCode: "",
        upfrontSalesforceCode: null,
        recurringSalesforceCode: null,
        notes: "",
        showOnQuote: true,
        isInternal: false,
        originalUpfrontCost: null,
        originalUpfrontPrice: null,
        originalRecurringCost: null,
        originalRecurringPrice: null,
        disabled: false,
        source: null,
        importTemplateId: null,
        moduleId: null,
        moduleRevisionId: null,
        basketModuleGuid: null,
        upfrontRecognitionMonths: null,
        originalRecurringNameOnQuote: "",
        originalUpfrontNameOnQuote: ""
    }
}

export const parseUserInputtedNumber = (inputValue, maxDecimalPlaces = 12) => {

    // Remove invalid characters (anything that's not digit, dot, or minus)
    var valueStrippedOfInvalidChars = inputValue.toString().replace(/[^0-9.-]/g, '');

    if (valueStrippedOfInvalidChars !== inputValue) {
        inputValue = valueStrippedOfInvalidChars;
    }

    // Ensure only one minus sign at the beginning
    if (inputValue.includes('-')) {
        var inputWithNegativeSymbolRemoved = inputValue.replace(/-/g, '');

        if (inputValue.charAt(0) === '-') {
            inputValue = '-' + inputWithNegativeSymbolRemoved
        } else {
            inputValue = inputWithNegativeSymbolRemoved
        }
    }

    var isNegative = false;

    if (inputValue.length > 0 && inputValue.charAt(0) === "-") {
        // Remove the leading negative symbol if there is one, and make a note that this number is negative.
        // We need to remove it, because later we are going to take all characters before the decimal point
        // and truncate to the first 12.  We only want the numbers when we do this.
        isNegative = true;
        inputValue = inputValue.slice(1)
    }

    if (inputValue.length > 0 && inputValue.charAt(0) === ".") {
        // If they start with a ".", then we insert a 0 before it
        inputValue = "0" + inputValue
    }

    // Ensure only one decimal point
    const parts = inputValue.split('.');

    var valueBeforeDecimalPoint = parts[0];

    if (valueBeforeDecimalPoint.length > 12) {
        // 12 digits max before decimal point
        valueBeforeDecimalPoint = valueBeforeDecimalPoint.slice(0, 12)
    }

    if (valueBeforeDecimalPoint === "" && parts.length > 1) {
        // If they started the input with a ".", then we automatically insert a "0" before it
        valueBeforeDecimalPoint = "0"
    }

    if (maxDecimalPlaces === 0) {
        // Discard any decimal places that the user entered, as this input doesn't support it
        inputValue = valueBeforeDecimalPoint
    } else if (parts.length > 1) {
        // At least one decimal point has been entered.  We need to take all digits after the first decimal
        // place and ignore any additional numbers after any additional decimal points, then truncate it to the
        // number of decimal places that this input supports.
        let decimalPortionToRequiredNumDecimalPlaces = parts[1];

        if (decimalPortionToRequiredNumDecimalPlaces.length > maxDecimalPlaces) {
            decimalPortionToRequiredNumDecimalPlaces = decimalPortionToRequiredNumDecimalPlaces.slice(0, maxDecimalPlaces)
        }

        inputValue = valueBeforeDecimalPoint + '.' + decimalPortionToRequiredNumDecimalPlaces;
    } else if (parts.length === 1) {
        // User didn't enter a number with decimal portion
        inputValue = valueBeforeDecimalPoint
    }

    if (isNegative) {
        inputValue = "-" + inputValue;
    }

    return inputValue
}

export const convertItemLookupTypesToColumnDefinitions = (itemLookupTypes) => {
    const getAttributeColumnWidth = (label) => {  
        const customAttr = itemLookupTypes.find(ilt => ilt.customAttributes?.some(f => f.label === label))?.customAttributes.find(f => f.label === label);
        const fieldType = customAttr?.fieldType;
        let widthBasedOnHeader = label.length * 3.2; // Estimate width based on header text length.  Aiming to have it wrap to three lines max

        switch (fieldType) {
            case "boolean":
                return Math.max(widthBasedOnHeader, 80) + "px";
            case "datetime":
                return Math.max(widthBasedOnHeader, 200) + "px";
            case "decimal":
                return Math.max(widthBasedOnHeader, 100) + "px";
            case "integer":
                return Math.max(widthBasedOnHeader, 80) + "px";
            case "lookup": {
                const choices = customAttr?.lookupChoices ?? [];
                const maxLen = choices.reduce((max, c) => Math.max(max, c?.length ?? 0), 0);

                const baseWidth = 60; // base width for dropdown arrow and padding
                const pixelsPerCharacter = 7; // approximate width of a character in pixels, not exact as each character can have different width but good enough for estimation
                const spaceForCharacters = maxLen * pixelsPerCharacter;
                const minWidth = 120;
                const maxWidth = 350;

                const width = Math.min(Math.max(spaceForCharacters + baseWidth, minWidth), maxWidth);
                return `${Math.max(widthBasedOnHeader, width)}px`;
            }
            case "text":
                return Math.max(widthBasedOnHeader, 250) + "px";
            default:
                return undefined;
        }
    }

    const itemLookupFields = itemLookupTypes.flatMap(itemLookupType => 
        itemLookupType.customAttributes
        .map(typeCustomAttribute => ({
            key: "TypeCustomAttr_" + typeCustomAttribute.label,
            label: typeCustomAttribute.label,
            align: ["integer", "decimal"].includes(typeCustomAttribute.fieldType.toLowerCase()) ? "right" : "left",
            group: "Custom Attributes",
            style: { width: getAttributeColumnWidth(typeCustomAttribute.label) }
        }))
    )

    // Remove duplicates based on label
    const uniqueFields = Array.from(
        new Map(itemLookupFields.map(field => [field.label, field])).values()
    );

    return uniqueFields;
}

/*
    The data passed in must have already been processed by the function GetItemsWithCalculatedProperties()
*/
export const NestQuoteItems = (data, quoteModules, salesforceCodes) => {
    const inconsistentCommercialFlags = data.filter(x => x?.commercialsAreOutdated && !x?.latestCommercials);
    if (inconsistentCommercialFlags.length > 0) {
        console.warn("[NestQuoteItems] Found item(s) marked as outdated without latestCommercials", inconsistentCommercialFlags.map(x => ({
            id: x?.id,
            guid: x?.guid,
            itemLookupId: x?.itemLookupId,
            itemLookupVersionId: x?.itemLookupVersionId,
            internalName: x?.internalName,
        })));
    }

    const validatedData = data.map(x => ({
        ...x,
        validationErrors: getItemErrors(x, salesforceCodes)
    }))

    console.log("nestQuoteItems:", data);
    console.log("nestQuoteModules:", quoteModules);

    const quoteModuleGuids = [...new Set(validatedData.map(item => item.quoteModuleGuid))];
    const manuallyAddedGroups = [...new Set(validatedData.filter(x => x.quoteModuleGuid === null).map(item => item.quoteGroupDescription))];
    var groupNumber = 1;

    var formattedData = [
        ...quoteModuleGuids.filter(x => x !== null).map(quoteModuleGuid => {
            var currentModule = quoteModules?.find(x => x.guid === quoteModuleGuid);
            var itemNum = 1;
            const moduleItems = validatedData.filter(x => x.quoteModuleGuid === quoteModuleGuid);

            return {
                id: quoteModuleGuid, //used by nestedDataTable to identify row
                selectable: true,
                itemNumber: (groupNumber++).toString(),
                guid: quoteModuleGuid,
                quoteModuleGuid: quoteModuleGuid,
                disabled: moduleItems.length > 0 && moduleItems.every(x => x.disabled), // group is disabled only when all of its child items are disabled
                topLevelGroupRow: true,
                description: data.find(x => x.quoteModuleGuid === quoteModuleGuid).quoteGroupDescription,
                isCreditItem: data.filter(x => x.quoteModuleGuid === quoteModuleGuid && x.isCreditItem).length === data.filter(x => x.quoteModuleGuid === quoteModuleGuid).length,
                itemLookupTypeId: null,
                validationErrors: [],
                notes: currentModule ? currentModule.inputValues.find(input => input.inputGuid === "notes")?.value ?? "" : "",
                items: moduleItems
                    .sort((a, b) => a.id - b.id)
                    .map(item => ({
                        ...item,
                        selectable: true,
                        itemNumber: (groupNumber - 1).toString() + "." + (itemNum++).toString(),
                        databaseId: item.id, 
                        id: quoteModuleGuid ? quoteModuleGuid + "__" + item.guid : item.guid,
                        description: item.internalName + (itemNum).toString()
                    })
                    )
            }
        })
    ]

    /// Standalone items with no parent module
    manuallyAddedGroups.forEach(group => {
        var itemNum = 1;
        const groupItems = validatedData.filter(x => x.quoteModuleGuid === null && x.quoteGroupDescription === group);

        formattedData = [
            ...formattedData,
            {
                id: group.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-_]/g, ''), //used by nestedDataTable to identify row.  Remove characters that aren't valid for DOM ids
                selectable: true,
                itemNumber: (groupNumber++).toString(),
                guid: group,
                quoteModuleGuid: null,
                disabled: groupItems.length > 0 && groupItems.every(x => x.disabled), // group is disabled only when all of its child items are disabled
                description: group,
                isCreditItem: false,
                topLevelGroupRow: true,
                itemLookupTypeId: null,
                validationErrors: [],
                items: groupItems
                    .sort((a, b) => a.id - b.id)
                    .map(item => ({
                        ...item,
                        selectable: true,
                        itemNumber: (groupNumber - 1).toString() + "." + (itemNum++).toString(),
                        databaseId: item.id, 
                        id: item.guid,
                        description: item.internalName
                    })
                    )
            }
        ]
    })

    // Now, for each item, we will copy across any typeCustomAttributes to be top-level properties on the item,
    formattedData = formattedData.map(x => ({
                        ...x,
                        items: (x.items || []).map(item => ({
                            ...item,
                            ...item.typeCustomAttributes?.map(attr => ({
                                ["TypeCustomAttr_" + attr.label]: attr.value
                            })).reduce((acc, curr) => ({ ...acc, ...curr }), {})
                        }))
                    }))

    return formattedData
}

export const getModuleErrors = (quoteModuleGuid, quoteModules, allModules) => {
    const quoteModuleToCheck = quoteModules.find(x => x.guid === quoteModuleGuid);
    if (!quoteModuleToCheck) return [{ type: "system", error: "Quote module not found" }];

    const moduleToCheck = allModules.find(x => x.id === quoteModuleToCheck.moduleId);
    if (!moduleToCheck) return [{ type: "system", error: "Module definition not found" }];

    const otherQuoteModules = quoteModules.filter(x => x.guid !== quoteModuleGuid);
    const otherModules = allModules.filter(x => otherQuoteModules.some(quoteModule => quoteModule.moduleId === x.id));

    // Extract relevant properties from the module and quoteModule, and cope with possibility that old 
    // modules may not have these properties in the DB yet
    const dependencies = moduleToCheck?.settings?.dependencies || [];
    const compatibility = moduleToCheck?.settings?.compatibility || [];
    const warnings = Array.isArray(quoteModuleToCheck.warnings) ? quoteModuleToCheck.warnings : [];

    // We will build up this array, as we search for errors
    const moduleErrors = [];

    // type can be:
    // "input" - input warning from module.  Must supply inputGuid
    // "dependency" - If module depends on another module, which isn't present on the quote. Must supply moduleId of required module
    // "compatibility" - Module is not compatible with another module in the quote. Must supply moduleId of incompatible module
    const addError = ({ type, id, error, detail, inputGuid, moduleId, acknowledgedBy }) => {
        moduleErrors.push({
            id,
            type,
            error,
            detail,
            inputGuid,
            moduleId,
            acknowledgedBy
        })
    }

    const quoteHasModule = (moduleId) => otherQuoteModules.some(otherModule => {
        const userInputs = otherModule.inputValues || [];
        const isCreditItem = userInputs.find(input => input.inputGuid === "isCreditItem")?.value === true;

        return !isCreditItem && (
            moduleId === otherModule.moduleId
        );
    })

    // Check if any module dependencies are missing
    dependencies.forEach(dependencyModuleId => {
        if (!quoteHasModule(dependencyModuleId)) {
            const requiredModuleName = allModules.find(x => x.id === dependencyModuleId)?.moduleName || "Unknown Module";
            addError({ type: "dependency", id: `${quoteModuleGuid}|${dependencyModuleId}`, error: `Module has missing dependencies`, detail: requiredModuleName, moduleId: dependencyModuleId });
        }
    });

    // Compatibility check is a little tricky because a module may have been added to the system before the module being checked was created, and therefore it wasn't
    // aware of it when compatibility was defined in it.  So, by default, new modules aren't compatible with any existing modules, unless explicitly defined as compatible.

    // look through all other modules that are on this quote and see if they are compatible with this module
    otherModules.forEach(otherModule => {
        let isCompatible = false;

        if (moduleToCheck.id === otherModule.id) {
            // Assume a module is always compatible with itself
            isCompatible = true;
        } else if (compatibility.includes(otherModule.id) || dependencies.includes(otherModule.id)) {
            // Compatibility check: this module defines which others it is compatible with, and this other module is one of them
            isCompatible = true;
        } else if (otherModule?.settings?.compatibility?.includes(moduleToCheck.id) || otherModule?.settings?.dependencies?.includes(moduleToCheck.id)) {
            // Compatibility check: the other module defines which modules it is compatible with, and this module is one of them
            isCompatible = true;
        }

        if (!isCompatible) {
            addError({ type: "compatibility", id: `${quoteModuleGuid}|${otherModule.id}`, error: `Module is not compatible with other module(s) added to the quote`, detail: otherModule.moduleName, moduleId: otherModule.id });
        }
    });

    // These are warnings that were presented to the user when they were configuring the module inputs.  They must have accepted them to be able to add the module to the quote.
    warnings.forEach(warning => {
        addError({ type: "input", id: `${quoteModuleGuid}|${warning.guid}`, error: `Input "${warning.inputLabel}" has warnings.`, detail: warning.message, inputGuid: warning.inputGuid, acknowledgedBy: warning.acknowledgedBy });
    });

    return moduleErrors;
}

export const isValidNumber = (value) => typeof value === 'number' && !isNaN(value);

export const getItemErrors = (item, salesforceCodes) => {
    var itemErrors = [];

    const addError = ({ id, field, error, severity, detail }) => {
        itemErrors.push({
            id: id || field,
            field,
            error: error,
            severity: severity || "warning",
            detail
        })
    }

    const addErrorIfDiscountExceedsThreshold = ({ currentValue, originalValue, thresholdPercent, field, fieldLabel }) => {
        if (!isValidNumber(currentValue) || !isValidNumber(originalValue) || !isValidNumber(thresholdPercent)) {
            return;
        }

        if (originalValue > 0 && currentValue < originalValue) {
            const discountPercent = ((originalValue - currentValue) / originalValue * 100)

            if (RoundToTwoDecimalPlaces(discountPercent) > RoundToTwoDecimalPlaces(thresholdPercent)) {
                addError({
                    id: `${field}DiscountExceedsThreshold`,
                    field,
                    error: `The discount applied to the ${fieldLabel} exceeds the allowed threshold`,
                    severity: "warning"
                })
            }
        }
    }

    if (item.commercialsAreOutdated && item.latestCommercials) {
        const getRecurrenceText = (item) => {
            if (item.isUpfront && item.isRecurring) {
                return "Upfront and Recurring";
            } else if (item.isUpfront) {
                return "Upfront";
            } else if (item.isRecurring) {
                return "Recurring";
            }
            return "None";
        }

        const currentUpfrontSalesforceCode = item.isUpfront ? item.upfrontSalesforceCode : "None";
        const latestUpfrontSalesforceCode = item.latestCommercials.isUpfront ? item.latestCommercials.upfrontSalesforceCode : "None";
        const upfrontSalesforceCodeChange = currentUpfrontSalesforceCode !== latestUpfrontSalesforceCode;

        const currentRecurringSalesforceCode = item.isRecurring ? item.recurringSalesforceCode : "None";
        const latestRecurringSalesforceCode = item.latestCommercials.isRecurring ? item.latestCommercials.recurringSalesforceCode : "None";
        const recurringSalesforceCodeChange = currentRecurringSalesforceCode !== latestRecurringSalesforceCode;

        const currentRecurrence = getRecurrenceText(item);
        const latestRecurrence = getRecurrenceText(item.latestCommercials);
        const upfrontRecurrenceChange = currentRecurrence !== latestRecurrence;

        const currentUpfrontListCost = item.isUpfront ? `£${formatCurrency(item.originalUpfrontCost)}` : "None";
        const latestUpfrontListCost = item.latestCommercials.isUpfront ? `£${formatCurrency(item.latestCommercials.upfrontCost)}` : "None";
        const upfrontListCostChange = latestUpfrontListCost !== currentUpfrontListCost;

        const currentUpfrontListPrice = item.isUpfront ? `£${formatCurrency(item.originalUpfrontPrice)}` : "None";
        const latestUpfrontListPrice = item.latestCommercials.isUpfront ? `£${formatCurrency(item.latestCommercials.upfrontPrice)}` : "None";
        const upfrontListPriceChange = latestUpfrontListPrice !== currentUpfrontListPrice;

        const currentRecurringListCost = item.isRecurring ? `£${formatCurrency(item.originalRecurringCost)}` : "None";
        const latestRecurringListCost = item.latestCommercials.isRecurring ? `£${formatCurrency(item.latestCommercials.recurringCost)}` : "None";
        const recurringListCostChange = latestRecurringListCost !== currentRecurringListCost;

        const currentRecurringListPrice = item.isRecurring ? `£${formatCurrency(item.originalRecurringPrice)}` : "None";
        const latestRecurringListPrice = item.latestCommercials.isRecurring ? `£${formatCurrency(item.latestCommercials.recurringPrice)}` : "None";
        const recurringListPriceChange = latestRecurringListPrice !== currentRecurringListPrice;

        const commercialChanges = [];        

        if (upfrontRecurrenceChange) {
            commercialChanges.push(`This catalogue item has changed from being "${currentRecurrence}" to being "${latestRecurrence}". `);
        }
        if (upfrontListCostChange) {
            commercialChanges.push(`Upfront Cost changed from ${currentUpfrontListCost} to ${latestUpfrontListCost}. `);
        }
        if (upfrontListPriceChange) {
            commercialChanges.push(`Upfront Price changed from ${currentUpfrontListPrice} to ${latestUpfrontListPrice}. `);
        }
        if (upfrontSalesforceCodeChange) {
            commercialChanges.push(`Upfront Salesforce Code changed from ${currentUpfrontSalesforceCode} to ${latestUpfrontSalesforceCode}. `);
        }
        if (recurringListCostChange) {
            commercialChanges.push(`Recurring Cost changed from ${currentRecurringListCost} to ${latestRecurringListCost}. `);
        }
        if (recurringListPriceChange) {
            commercialChanges.push(`Recurring Price changed from ${currentRecurringListPrice} to ${latestRecurringListPrice}. `);
        }
        if (recurringSalesforceCodeChange) {
            commercialChanges.push(`Recurring Salesforce Code changed from ${currentRecurringSalesforceCode} to ${latestRecurringSalesforceCode}. `);
        }

        addError({ 
            id: "commercialsOutdated", 
            field: "commercialsAreOutdated", 
            error: "The commercial values for this item are outdated.  Please update to the latest commercials or confirm that the current values are correct.", 
            severity: "warning",
            detail: commercialChanges.length > 0 ? "<ul><li>" + commercialChanges.join("</li><li>") + "</li></ul>" : ""
        })
    }

    if (!item.internalName) { addError({ id: "internalNameWasNotSupplied", field: "internalName", error: "Internal Name wasn't supplied.", severity: "error" }) }
    if (!item.quantity) { addError({ id: "quantityShouldNotBeZero", field: "quantity", error: "Quantity should be not be zero", severity: "warning" }) }

    if (!item.supplierId) { addError({ id: "supplierIdIsRequired", field: "supplierId", error: "Supplier was not supplied", severity: "warning" }) }
    if (!item.autotaskCode) { addError({ id: "autotaskCodeIsRequired", field: "autotaskCode", error: "Autotask Code was not supplied", severity: "warning" }) }

    if (item.isUpfront) {
        if (item.upfrontCost == null || item.upfrontCost === "") { addError({ id: "upfrontCostIsRequired", field: "upfrontCost", error: "Upfront Cost value is required", severity: "error" }) }
        if (item.upfrontPrice == null || item.upfrontPrice === "") { addError({ id: "upfrontPriceIsRequired", field: "upfrontPrice", error: "Upfront Price value is required", severity: "error" }) }
        if (!item.upfrontNameOnQuote) { addError({ id: "upfrontNameOnQuoteWasNotSupplied", field: "upfrontNameOnQuote", error: "Upfront Name on Quote wasn't supplied. So will use the internal name", severity: "warning" }) }

        if (salesforceCodes.some(x => x.name === item.upfrontSalesforceCode && x.type === "Recurring" && x.canBePaidUpfront)) {
            if (!item.upfrontRecognitionMonths) {
                addError({ id: "upfrontRecognitionMonthsRequired", field: "upfrontRecognitionMonths", error: "Recognition Period is required for the selected upfront Salesforce Code", severity: "error" })
            } else if (item.upfrontRecognitionMonths < 12) {
                addError({ id: "upfrontRecognitionMonthsMustBeAtLeast12", field: "upfrontRecognitionMonths", error: "Recognition Period (Months) must be at least 12", severity: "error" })
            } else if (item.upfrontRecognitionMonths > 999) {
                addError({ id: "upfrontRecognitionMonthsMustBeAtMost999", field: "upfrontRecognitionMonths", error: "Recognition Period (Months) max value is 999", severity: "error" })
            }
        }

        if (!item.exemptFromMarginWarnings &&
            RoundToTwoDecimalPlaces(item.upfrontMargin) < 20 &&
            item.upfrontCost > 0 && !item.isCreditItem) {
            addError({ id: "upfrontMarginMustBeAtLeast20", field: "upfrontMargin", error: "Upfront Margin must be at least 20%", severity: "warning" })
        }

        addErrorIfDiscountExceedsThreshold({ currentValue: item.upfrontPrice, originalValue: item.originalUpfrontPrice, thresholdPercent: item.upfrontPriceDiscountThreshold, field: "upfrontPrice", fieldLabel: "upfront price" });
        addErrorIfDiscountExceedsThreshold({ currentValue: item.upfrontCost, originalValue: item.originalUpfrontCost, thresholdPercent: item.upfrontCostDiscountThreshold, field: "upfrontCost", fieldLabel: "upfront cost" });

        if (!item.upfrontSalesforceCode) {
            addError({ id: "upfrontSalesforceCodeIsRequired", field: "upfrontSalesforceCode", error: "Upfront Salesforce Code is required", severity: "error" })
        } else if (!salesforceCodes.filter(x => x.type === "Upfront" || (x.type === "Recurring" && x.canBePaidUpfront)).some(x => x.name === item.upfrontSalesforceCode)) {
            addError({ id: "upfrontSalesforceCodeInvalid", field: "upfrontSalesforceCode", error: "Upfront Salesforce Code is invalid", severity: "error" })
        }
    }

    if (item.isRecurring) {
        if (item.recurringCost == null || item.recurringCost === "") { addError({ id: "recurringCostIsRequired", field: "recurringCost", error: "Recurring Cost value is required", severity: "error" }) }
        if (item.recurringPrice == null || item.recurringPrice === "") { addError({ id: "recurringPriceIsRequired", field: "recurringPrice", error: "Recurring Price value is required", severity: "error" }) }
        if (!item.recurringNameOnQuote) { addError({ id: "recurringNameOnQuoteWasNotSupplied", field: "recurringNameOnQuote", error: "Recurring Name on Quote wasn't supplied. So will use the internal name", severity: "warning" }) }

        if (!item.exemptFromMarginWarnings &&
            RoundToTwoDecimalPlaces(item.recurringMargin) < 20 &&
            item.recurringCost > 0 && !item.isCreditItem) {
            addError({ id: "recurringMarginMustBeAtLeast20", field: "recurringMargin", error: "Recurring Margin must be at least 20%", severity: "warning" })
        }

        addErrorIfDiscountExceedsThreshold({ currentValue: item.recurringPrice, originalValue: item.originalRecurringPrice, thresholdPercent: item.recurringPriceDiscountThreshold, field: "recurringPrice", fieldLabel: "recurring price" });
        addErrorIfDiscountExceedsThreshold({ currentValue: item.recurringCost, originalValue: item.originalRecurringCost, thresholdPercent: item.recurringCostDiscountThreshold, field: "recurringCost", fieldLabel: "recurring cost" });

        if (!item.recurringStartMonth) { addError({ id: "recurringStartMonthIsRequired", field: "recurringStartMonth", error: "Start Month value is required", severity: "error" }) }
        if (item.recurringStartMonth < 1) { addError({ id: "recurringStartMonthMustBeGreaterThan0", field: "recurringStartMonth", error: "Start Month must be greater than 0", severity: "error" }) }
        if (item.recurringStartMonth > 999) { addError({ id: "recurringStartMonthMustBeAtMost999", field: "recurringStartMonth", error: "Start Month max value is 999", severity: "error" }) }

        if (!item.recurringIntervalMonths) { addError({ id: "recurringIntervalMonthsIsRequired", field: "recurringIntervalMonths", error: "Interval (Months) value is required", severity: "error" }) }
        if (item.recurringIntervalMonths < 1) { addError({ id: "recurringIntervalMonthsMustBeGreaterThan0", field: "recurringIntervalMonths", error: "Interval (Months) must be greater than 0", severity: "error" }) }
        if (item.recurringIntervalMonths > 999) { addError({ id: "recurringIntervalMonthsMustBeAtMost999", field: "recurringIntervalMonths", error: "Interval (Months) max value is 999", severity: "error" }) }

        if (!item.recurringNumPayments) { addError({ id: "recurringNumPaymentsIsRequired", field: "recurringNumPayments", error: "Num Payments value is required", severity: "error" }) }
        if (item.recurringNumPayments < 1) { addError({ id: "recurringNumPaymentsMustBeGreaterThan0", field: "recurringNumPayments", error: "Num Payments must be greater than 0", severity: "error" }) }
        if (item.recurringNumPayments > 999) { addError({ id: "recurringNumPaymentsMustBeAtMost999", field: "recurringNumPayments", error: "Num Payments max value is 999", severity: "error" }) }

        if (!item.recurringSalesforceCode) {
            addError({ id: "recurringSalesforceCodeIsRequired", field: "recurringSalesforceCode", error: "Recurring Salesforce Code is required", severity: "error" })
        } else if (!salesforceCodes.filter(x => x.type === "Recurring").some(x => x.name === item.recurringSalesforceCode)) {
            addError({ id: "recurringSalesforceCodeInvalid", field: "recurringSalesforceCode", error: "Recurring Salesforce Code is invalid", severity: "error" })
        }
    }
    // Filter to keep only the first error for each field
    const seenFields = new Set();
    const filteredErrors = itemErrors.filter(({ field }) => {
        if (seenFields.has(field)) return false;
        seenFields.add(field);
        return true;
    });

    return filteredErrors;
}

const makeZeroIfNotNumber = (n) => Number.isFinite(n) ? n : 0;
const BROADBAND_BTW_PXC_ITEM_LOOKUP_TYPE_ID = 13;

export const getCalculatedPropertiesForItems = (items, itemLookups, salesforceCodes) => {
    return items.map(item => {
        const quantity = item.quantity ?? 1;
        const multiplier = item.isCreditItem ? -quantity : quantity;

        // Normalize undefined/null to 0 for pricing values
        const upfrontPrice = item.isUpfront ? item.upfrontPrice ?? 0 : 0;
        const upfrontCost = item.isUpfront ? item.upfrontCost ?? 0 : 0;
        const recurringPrice = item.isRecurring ? item.recurringPrice ?? 0 : 0;
        const recurringCost = item.isRecurring ? item.recurringCost ?? 0 : 0;

        // This field used to be a custom attribute, but we promoted it to be a top-level field on the item, so that it's easier to work with.
        // For any items that were created before this change, we will still look for the value in the custom attributes, so that we don't lose any data.
        const upfrontRecognitionMonths = item.upfrontRecognitionMonths ?? item.typeCustomAttributes?.find(attr => attr.label === "Spread Upfront Term Length for reporting in Months")?.value;

        const upfrontPriceDiscountValue = item.isUpfront && item.originalUpfrontPrice > item.upfrontPrice ? item.originalUpfrontPrice - item.upfrontPrice ?? 0 : 0;
        const recurringPriceDiscountValue = item.isRecurring && item.originalRecurringPrice && item.recurringPrice && item.originalRecurringPrice > item.recurringPrice ? item.originalRecurringPrice - item.recurringPrice : 0;
        const upfrontPriceDiscountPercent = item.isUpfront && item.originalUpfrontPrice > 0 && item.originalUpfrontPrice > item.upfrontPrice ? (upfrontPriceDiscountValue / item.originalUpfrontPrice) * 100 : 0;
        const recurringPriceDiscountPercent = item.isRecurring && item.originalRecurringPrice > 0 && item.originalRecurringPrice > item.recurringPrice ? (recurringPriceDiscountValue / item.originalRecurringPrice) * 100 : 0;

        const upfrontMarginValue = upfrontPrice !== 0 && item.isUpfront ? (upfrontPrice - upfrontCost) : 0;
        const recurringMarginValue = recurringPrice !== 0 && item.isRecurring ? (recurringPrice - recurringCost) : 0;

        var upfrontCostDiscountThreshold = 0;
        var upfrontPriceDiscountThreshold = 0;
        var recurringCostDiscountThreshold = 0;
        var recurringPriceDiscountThreshold = 0;

        var latestUpfrontCost = null;
        var latestRecurringCost = null;
        var latestUpfrontPrice = null;
        var latestRecurringPrice = null;
        var exemptFromMarginWarnings = false;
        var commercialsAreOutdated = false;
        var latestCommercials = null;

        const itemInCatalog = item.itemLookupId ? itemLookups.find(x => x.id === item.itemLookupId) : null;

        const isBroadbandBTWorPXC = item.itemLookupTypeId === BROADBAND_BTW_PXC_ITEM_LOOKUP_TYPE_ID;

        if (itemInCatalog && itemInCatalog.itemLookupVersions.length > 0) {
            exemptFromMarginWarnings = itemInCatalog.exemptFromMarginWarnings;

            const getClosestPastVersion = (versions) =>
                versions
                    .filter(r => new Date(r.activeStartDate) < new Date())
                    .sort((a, b) => {
                        const startDiff = new Date(b.activeStartDate) - new Date(a.activeStartDate);
                        if (startDiff !== 0) return startDiff;
                        return new Date(b.createdAt) - new Date(a.createdAt);
                    })[0] || null;

            var closestVersion = getClosestPastVersion(itemInCatalog.itemLookupVersions);

            if (closestVersion) {
                upfrontCostDiscountThreshold = closestVersion.upfrontCostDiscountThreshold;
                upfrontPriceDiscountThreshold = closestVersion.upfrontPriceDiscountThreshold;
                recurringCostDiscountThreshold = closestVersion.recurringCostDiscountThreshold;
                recurringPriceDiscountThreshold = closestVersion.recurringPriceDiscountThreshold;
                latestUpfrontCost = closestVersion.isUpfront ? closestVersion.upfrontCost : null;
                latestRecurringCost = closestVersion.isRecurring ? closestVersion.recurringCost : null;
                latestUpfrontPrice = closestVersion.isUpfront ? closestVersion.upfrontPrice : null;
                latestRecurringPrice = closestVersion.isRecurring ? closestVersion.recurringPrice : null;

                latestCommercials = isBroadbandBTWorPXC
                    ? {
                        ...closestVersion,
                        supplierId: itemInCatalog.supplierId,

                        // BTW/PXC Autotask code is generated from quote attributes, not catalogue default.
                        autotaskCode: item.autotaskCode
                    }
                    : {
                        ...closestVersion,
                        autotaskCode: itemInCatalog.autotaskCode,
                        supplierId: itemInCatalog.supplierId
                    };
                commercialsAreOutdated = item.itemLookupVersionId !== closestVersion.id;
            } else {
                console.warn("No valid version found for item lookup " + itemInCatalog.name);
            }

            // BTW/PXC Autotask codes are generated from selected attributes on the quote.
            if ((itemInCatalog.autotaskCode !== item.autotaskCode && !isBroadbandBTWorPXC) ||
                itemInCatalog.supplierId !== item.supplierId
            ) {
                commercialsAreOutdated = true;

                if (!latestCommercials) {
                    latestCommercials = {};
                }

                latestCommercials = {
                    ...latestCommercials,
                    autotaskCode: isBroadbandBTWorPXC ? item.autotaskCode : itemInCatalog.autotaskCode,
                    supplierId: itemInCatalog.supplierId
                }
            }
        } else if (item.itemLookupId) {
            // We haven't found the item in the catalogue. Presumably we haven't loaded the cache yet.  

            // We will assume, for now, that it is exempt from margin warnings.  Once the catalogue item is loaded,
            // we will re-run the calculations and update the item with the correct value for exemptFromMarginWarnings.
            exemptFromMarginWarnings = true; 
        }

        if (isBroadbandBTWorPXC) {
            // Temporary fix to never show outdated commercials for BTW/PXC items, as the Autotask code is generated
            // from quote attributes, not catalogue defaults, and also the imported items can differ in terms of upfront
            // and recurring costs/prices from the catalogue defaults, so we will ignore those differences for now. 
            latestCommercials = null;
            commercialsAreOutdated = false;
        }

        // Treat 0, null, or undefined recurringNumPayments as invalid
        const validRecurringPayments = !item.isRecurring || (Number.isFinite(item.recurringNumPayments) && item.recurringNumPayments > 0 && item.recurringIntervalMonths > 0);

        const upfrontMargin = !item.isUpfront
            ? NaN
            : upfrontPrice !== 0
                ? ((upfrontPrice - upfrontCost) / upfrontPrice) * 100 * (item.isCreditItem ? -1 : 1)
                : 0;

        const recurringMargin = !validRecurringPayments || !item.isRecurring
            ? NaN
            : recurringPrice !== 0
                ? ((recurringPrice - recurringCost) / recurringPrice) * 100 * (item.isCreditItem ? -1 : 1)
                : 0;

        const totalUpfrontCost = upfrontCost * multiplier
        const totalUpfrontPrice = upfrontPrice * multiplier
        const totalUpfrontMarginValue = upfrontMarginValue * multiplier

        const paymentsPerYear = validRecurringPayments && item.isRecurring
            ? 12 / item.recurringIntervalMonths
            : NaN;

        const getFormattedIntervalDescription = () => {
            if (!validRecurringPayments) return "Invalid";
            if (!item.isUpfront && !item.isRecurring) return ""; // Should not happen, but maybe a bad import could do this
            if (!item.isRecurring) return "Upfront";

            if (item.recurringNumPayments === 1) {
                if (item.recurringIntervalMonths < 12 || item.recurringIntervalMonths % 12 !== 0) {
                    return "Once for " + item.recurringIntervalMonths + " month" + (item.recurringIntervalMonths > 1 ? "s" : "");
                } else {
                    return "Once for " + (item.recurringIntervalMonths / 12) + " year" + (item.recurringIntervalMonths / 12 > 1 ? "s" : "");
                }
            }

            let returnVal = "";

            if (paymentsPerYear === 1) {
                returnVal = "Yearly";
            } else if (paymentsPerYear === 12) {
                returnVal = "Monthly";
            } else if (paymentsPerYear === 4) {
                returnVal = "Quarterly";
            } else if (paymentsPerYear === 2) {
                returnVal = "Bi-annually";
            } else if (paymentsPerYear > 1  && Number.isInteger(paymentsPerYear)) {
                returnVal = paymentsPerYear + " times per year";
            } else if (paymentsPerYear > 1  && !Number.isInteger(paymentsPerYear)) {
                returnVal = paymentsPerYear.toFixed(2) + " times per year";
            } else {
                returnVal = `Every ${item.recurringIntervalMonths} month${item.recurringIntervalMonths > 1 ? "s" : ""}`;
            }

            return returnVal;
        }

        const intervalDescription = getFormattedIntervalDescription();

        const getFormattedBillingDescription = () => {
            if (!validRecurringPayments) return "Invalid";
            if (!item.isRecurring && !item.isUpfront) return ""; // Should not happen, but maybe a bad import could do this
            if (!item.isRecurring) return "Upfront";

            let returnVal = intervalDescription;

            if (item.recurringNumPayments === 1) {
                // If there's only 1 payment we don't need to say how many payments, as the description already says "Once"
            } else if (item.recurringNumPayments % paymentsPerYear === 0) {
                returnVal += " for "
                const numYears = item.recurringNumPayments / paymentsPerYear;
                returnVal += numYears + (numYears === 1 ? " year" : " years");
            } else if (item.recurringIntervalMonths === 1) {
                returnVal += " for "
                const numMonths = item.recurringNumPayments;
                returnVal += numMonths + (numMonths === 1 ? " month" : " months");
            } else {
                returnVal += " with "
                returnVal += item.recurringNumPayments + " payments";
            }

            if (item.recurringStartMonth && item.recurringStartMonth > 1) {
                if ((item.recurringStartMonth - 1) % 12 === 0) {
                    returnVal += " (Starting year " + (((item.recurringStartMonth - 1) / 12) + 1) + ")";
                } else {
                    returnVal += " (Starting month " + item.recurringStartMonth + ")";
                }
            }

            return returnVal;
        }

        const billingDescription = getFormattedBillingDescription();

        const getFormattedTermLengthDescription = () => {
            if (!validRecurringPayments) return "Invalid";
            if (!item.isRecurring) return "";

            const months = (item.recurringNumPayments * item.recurringIntervalMonths) + (item.recurringStartMonth || 1) - 1;

            if (isNaN(months) || months <= 0) {
                return "";
            }

            if (months % 12 === 0) {
                const years = months / 12;
                return `${years} year${years > 1 ? "s" : ""}`
            }

            return `${months} month${months > 1 ? "s" : ""}`            
        }

        const termLengthDescription = getFormattedTermLengthDescription();

        const startDelayDescription = !item.isRecurring
            ? ""
            : !item.recurringStartMonth 
                ? "Invalid"
                : item.recurringStartMonth > 1
                    ? `${item.recurringStartMonth - 1} month${item.recurringStartMonth - 1 > 1 ? "s" : ""}`
                    : "";

        // Should cope with payment terms of less than a year
        const annualPrice = validRecurringPayments && item.isRecurring
            ? recurringPrice * Math.min(paymentsPerYear, item.recurringNumPayments)
            : NaN;

        const annualCost = validRecurringPayments && item.isRecurring
            ? recurringCost * Math.min(paymentsPerYear, item.recurringNumPayments)
            : NaN;

        const annualMarginValue = validRecurringPayments && item.isRecurring
            ? annualPrice - annualCost
            : NaN;

        const totalAnnualCost = validRecurringPayments && item.isRecurring
            ? recurringCost * Math.min(paymentsPerYear, item.recurringNumPayments) * multiplier
            : NaN;

        const totalAnnualPrice = validRecurringPayments && item.isRecurring
            ? recurringPrice * Math.min(paymentsPerYear, item.recurringNumPayments) * multiplier
            : NaN;

        const totalAnnualPriceDiscountValue = validRecurringPayments && item.isRecurring
            ? (item.originalRecurringPrice - recurringPrice) * Math.min(paymentsPerYear, item.recurringNumPayments) * multiplier
            : NaN;

        const totalContractPriceDiscountValue = (upfrontPriceDiscountValue * multiplier) + makeZeroIfNotNumber(totalAnnualPriceDiscountValue);

        const totalAnnualMarginValue = annualMarginValue * multiplier;

        const totalRecurringCost = validRecurringPayments && item.isRecurring
            ? recurringCost * item.recurringNumPayments * multiplier
            : NaN;

        const totalRecurringPrice = validRecurringPayments && item.isRecurring
            ? recurringPrice * item.recurringNumPayments * multiplier
            : NaN;

        const totalRecurringMarginValue = recurringMarginValue * multiplier * item.recurringNumPayments

        const totalContractCost = makeZeroIfNotNumber(totalUpfrontCost) + makeZeroIfNotNumber(totalRecurringCost)
        const totalContractPrice = makeZeroIfNotNumber(totalUpfrontPrice) + makeZeroIfNotNumber(totalRecurringPrice);
        const totalContractMarginValue = makeZeroIfNotNumber(totalUpfrontPrice - totalUpfrontCost) + makeZeroIfNotNumber(totalRecurringPrice - totalRecurringCost)

        const termLengthMonths = !item.isRecurring ? 0 : ((item.recurringNumPayments ?? 0) * (item.recurringIntervalMonths ?? 0)) + (item.recurringStartMonth ?? 0) - 1

        const years = [];
        const numYears = Math.ceil(termLengthMonths / 12);

        for (let year = 1; year <= numYears; year++) {
            years.push(year);
        }

        // Always need at least year 1, just to put upfront things in, if there's no recurring
        if (years.length === 0) { years.push(1); }

        const statsByYear = {}

        const numPaymentsInYear = (year) => {
            if (!item.isRecurring) return 0;

            const startMonth = item.recurringStartMonth ?? 1;
            const interval = item.recurringIntervalMonths ?? 0;
            const totalPayments = item.recurringNumPayments ?? 0;

            if (!startMonth || !interval || !totalPayments) return 0;

            const yearStartMonth = (year - 1) * 12 + 1;
            const yearEndMonth = year * 12;

            let count = 0;
            for (let i = 0; i < totalPayments; i++) {
                const paymentMonth = startMonth + i * interval;
                if (paymentMonth >= yearStartMonth && paymentMonth <= yearEndMonth) {
                    count++;
                }
            }

            return count;
        };

        years.forEach(year => {
            const numPayments = numPaymentsInYear(year);
            const recurringCostTotal = numPayments * recurringCost * multiplier;
            const recurringPriceTotal = numPayments * recurringPrice * multiplier;

            statsByYear[year] = {
                totalUpfrontCost: year === 1 ? totalUpfrontCost : 0,
                totalUpfrontPrice: year === 1 ? totalUpfrontPrice : 0,
                totalRecurringCost: recurringCostTotal,
                totalRecurringPrice: recurringPriceTotal,
                totalInternalRecurringCost: item.isInternal ? recurringCostTotal : 0,
                totalExternalRecurringCost: !item.isInternal ? recurringCostTotal : 0,
                totalInternalRecurringPrice: item.isInternal ? recurringPriceTotal : 0,
                totalExternalRecurringPrice: !item.isInternal ? recurringPriceTotal : 0
            };

            statsByYear[year].totalUpfrontMargin = statsByYear[year].totalUpfrontPrice - statsByYear[year].totalUpfrontCost;
            statsByYear[year].totalRecurringMargin = statsByYear[year].totalRecurringPrice - statsByYear[year].totalRecurringCost;
            statsByYear[year].totalUpfrontRLEC = RoundToTwoDecimalPlaces(statsByYear[year].totalUpfrontPrice - statsByYear[year].totalExternalUpfrontCost);
            statsByYear[year].totalRecurringRLEC = RoundToTwoDecimalPlaces(statsByYear[year].totalRecurringPrice - statsByYear[year].totalExternalRecurringCost);
        });

        const getBusinessCOE = (salesforceCode) => {
            const sfCodeToFind = salesforceCode?.trim().toLowerCase();
            const sfCode = salesforceCodes.find(x => x.name.trim().toLowerCase() === sfCodeToFind);

            return sfCode?.businessCoe || (salesforceCode ? 'Unknown SF Code (' + salesforceCode + ')' : "(Empty)");
        }

        if (commercialsAreOutdated && !latestCommercials) {
            console.warn("[getCalculatedPropertiesForItems] Inconsistent commercial state detected", {
                id: item?.id,
                guid: item?.guid,
                itemLookupId: item?.itemLookupId,
                itemLookupVersionId: item?.itemLookupVersionId,
                internalName: item?.internalName,
            });
        }

        return {
            ...item,
            upfrontMargin,
            upfrontMarginValue,
            recurringMargin,
            recurringMarginValue,
            upfrontPriceDiscountValue: upfrontPriceDiscountValue,
            recurringPriceDiscountValue: recurringPriceDiscountValue,
            upfrontPriceDiscountPercent: upfrontPriceDiscountPercent,
            recurringPriceDiscountPercent: recurringPriceDiscountPercent,
            totalUpfrontMargin: upfrontMargin, // Quantity has no effect on margin percentage
            totalUpfrontMarginValue,
            totalRecurringMargin: recurringMargin, // Quantity has no effect on margin percentage
            totalRecurringMarginValue,
            totalUpfrontPriceDiscountPercent: upfrontPriceDiscountPercent, // Quantity has no effect on discount percentage
            totalRecurringPriceDiscountPercent: recurringPriceDiscountPercent, // Quantity has no effect on discount percentage
            totalUpfrontPriceDiscountValue: upfrontPriceDiscountValue * multiplier,
            totalRecurringPriceDiscountValue: recurringPriceDiscountValue * multiplier * item.recurringNumPayments,
            totalOriginalUpfrontCost: (item.originalUpfrontCost ?? 0) * multiplier,
            totalOriginalUpfrontPrice: (item.originalUpfrontPrice ?? 0) * multiplier,
            totalOriginalRecurringCost: (item.originalRecurringCost ?? 0) * multiplier * item.recurringNumPayments,
            totalOriginalRecurringPrice: (item.originalRecurringPrice ?? 0) * multiplier * item.recurringNumPayments,
            totalUpfrontCost,
            totalUpfrontPrice,
            annualCost,
            annualPrice,
            annualMargin: recurringMargin, // Quantity has no effect on margin percentage
            annualMarginValue,
            totalAnnualCost,
            totalAnnualPrice,
            totalAnnualPriceDiscountValue,
            totalAnnualPriceDiscountPercent: recurringPriceDiscountPercent, // Quantity has no effect on discount percentage
            totalAnnualMargin: recurringMargin, // Quantity has no effect on margin percentage
            totalAnnualMarginValue,
            totalRecurringCost,
            totalRecurringPrice,
            totalContractCost,
            totalContractPrice,
            totalContractMargin: totalContractPrice > 0 ? RoundToTwoDecimalPlaces((totalContractMarginValue / totalContractPrice) * 100) : 0,
            totalContractMarginValue,
            totalContractPriceDiscountValue,
            totalContractPriceDiscountPercent: totalContractPrice > 0 ? RoundToTwoDecimalPlaces((totalContractPriceDiscountValue / totalContractPrice) * 100) : 0,
            upfrontCostDiscountThreshold,
            upfrontPriceDiscountThreshold,
            recurringCostDiscountThreshold,
            recurringPriceDiscountThreshold,
            latestUpfrontCost,
            latestRecurringCost,
            latestUpfrontPrice,
            latestRecurringPrice,
            latestCommercials,
            commercialsAreOutdated: !!latestCommercials && commercialsAreOutdated,
            termLengthMonths,
            businessCOE: item.isRecurring && item.isUpfront && item.upfrontSalesforceCode !== item.recurringSalesforceCode ? getBusinessCOE(item.upfrontSalesforceCode) + " / " + getBusinessCOE(item.recurringSalesforceCode) : item.isRecurring ? getBusinessCOE(item.recurringSalesforceCode) : getBusinessCOE(item.upfrontSalesforceCode),
            upfrontBusinessCOE: getBusinessCOE(item.upfrontSalesforceCode),
            recurringBusinessCOE: getBusinessCOE(item.recurringSalesforceCode),
            startDelayDescription,
            intervalDescription,
            billingDescription,
            termLengthDescription,
            statsByYear,
            exemptFromMarginWarnings,
            upfrontRecognitionMonths
        };
    });
}

// We can use this function to strip out all the calculated properties, when we want to save items back
// to the database, so that we are only sending the user-inputted values (or system generated ids),
// and not any calculated values that are not stored on the backend anyway.
export const stripCalculatedPropertiesFromItems = (items) => {
    return items.map(item => {
        const strippedItem = { ...item };

        delete strippedItem.lineEditorId;
        delete strippedItem.validationErrors;
        delete strippedItem.upfrontMargin;
        delete strippedItem.upfrontMarginValue;
        delete strippedItem.recurringMargin;
        delete strippedItem.recurringMarginValue;
        delete strippedItem.upfrontPriceDiscountValue;
        delete strippedItem.recurringPriceDiscountValue;
        delete strippedItem.upfrontPriceDiscountPercent;
        delete strippedItem.recurringPriceDiscountPercent;
        delete strippedItem.totalUpfrontMargin;
        delete strippedItem.totalUpfrontMarginValue;
        delete strippedItem.totalRecurringMargin;
        delete strippedItem.totalRecurringMarginValue;
        delete strippedItem.totalUpfrontPriceDiscountPercent;
        delete strippedItem.totalRecurringPriceDiscountPercent;
        delete strippedItem.totalUpfrontPriceDiscountValue;
        delete strippedItem.totalRecurringPriceDiscountValue;
        delete strippedItem.totalOriginalUpfrontCost;
        delete strippedItem.totalOriginalUpfrontPrice;
        delete strippedItem.totalOriginalRecurringCost;
        delete strippedItem.totalOriginalRecurringPrice;
        delete strippedItem.totalUpfrontCost;
        delete strippedItem.totalUpfrontPrice;
        delete strippedItem.annualCost;
        delete strippedItem.annualPrice;
        delete strippedItem.annualMargin;
        delete strippedItem.annualMarginValue;
        delete strippedItem.totalAnnualCost;
        delete strippedItem.totalAnnualPrice;
        delete strippedItem.totalAnnualPriceDiscountValue;
        delete strippedItem.totalAnnualPriceDiscountPercent;
        delete strippedItem.totalAnnualMargin;
        delete strippedItem.totalAnnualMarginValue;
        delete strippedItem.totalRecurringCost;
        delete strippedItem.totalRecurringPrice;
        delete strippedItem.totalContractCost;
        delete strippedItem.totalContractPrice;
        delete strippedItem.totalContractMargin;
        delete strippedItem.totalContractMarginValue;
        delete strippedItem.totalContractPriceDiscountValue;
        delete strippedItem.totalContractPriceDiscountPercent;
        delete strippedItem.upfrontCostDiscountThreshold;
        delete strippedItem.upfrontPriceDiscountThreshold;
        delete strippedItem.recurringCostDiscountThreshold;
        delete strippedItem.recurringPriceDiscountThreshold;
        delete strippedItem.latestUpfrontCost;
        delete strippedItem.latestRecurringCost;
        delete strippedItem.latestUpfrontPrice;
        delete strippedItem.latestRecurringPrice;
        delete strippedItem.latestCommercials;
        delete strippedItem.commercialsAreOutdated;
        delete strippedItem.itemInCatalog;
        delete strippedItem.termLengthMonths;
        delete strippedItem.startDelayDescription;
        delete strippedItem.intervalDescription;
        delete strippedItem.billingDescription;
        delete strippedItem.termLengthDescription;
        delete strippedItem.businessCOE;
        delete strippedItem.upfrontBusinessCOE;
        delete strippedItem.recurringBusinessCOE;
        delete strippedItem.statsByYear;
        delete strippedItem.exemptFromMarginWarnings;

        return strippedItem;
    });
}

export const stripCalculatedPropertiesFromItem = (item) =>
    stripCalculatedPropertiesFromItems([item])[0];

export const getDefaultValueForInput = (input) => {
    switch (input.type.toLowerCase()) {
        case "text":
            return input.defaultValue ? input.defaultValue : ""
        case "number":
            return input.defaultValue ? Number(input.defaultValue).toFixed(input.decimalPlaces) : Number(0).toFixed(input.decimalPlaces)
        case "text area":
            return ""
        case "checkbox":
            return false
        case "select":
        case "radio":
            return input.defaultValue ? input.defaultValue : null
    }
}

export const getDefaultInputValues = (inputs) => {
    return inputs.map(input => ({
        inputGuid: input.guid,
        value: getDefaultValueForInput(input)
    }))
}


export const formatCurrencyForTable = (value) => {
    return value === null || isNaN(value) || value === 0 ? "-" : `£${formatCurrency(value)}`;
}

export const formatPercentageForTable = (value) => {
    return value === null || isNaN(value) || value === 0 ? "-" : `${value.toFixed(2)}%`;
}


export const logDifferences = (obj1, obj2) => {
    const allKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);

    allKeys.forEach(key => {
        const hasKey1 = obj1.hasOwnProperty(key);
        const hasKey2 = obj2.hasOwnProperty(key);
        const val1 = obj1[key];
        const val2 = obj2[key];

        if (hasKey1 && !hasKey2) {
            console.log(`Removed property '${key}':`, { from: val1, to: undefined });
        } else if (!hasKey1 && hasKey2) {
            console.log(`Added property '${key}':`, { from: undefined, to: val2 });
        } else {
            const areObjects = typeof val1 === 'object' && val1 !== null && typeof val2 === 'object' && val2 !== null;
            const isEqual = areObjects
                ? JSON.stringify(val1) === JSON.stringify(val2)
                : val1 === val2;

            if (!isEqual) {
                console.log(`Changed property '${key}':`, { from: val1, to: val2 });
            }
        }
    });
}

export const sanitizeForId = (input) => {
    return input
        .toString()                      // Ensure it's a string
        .toLowerCase()                  // Optional: normalize casing
        .replace(/\s+/g, '-')           // Replace spaces with hyphens
        .replace(/[^a-z0-9\-\_]/g, ''); // Remove all disallowed characters
}

// shared function to generate quote file name
export const getQuoteFileName = (quoteId, revisionNumber, opportunityName) => {
    return `Quote${quoteId}-${revisionNumber} ${opportunityName}.docx`;
}